import React, { useCallback, useEffect, useRef, useState } from 'react';

/* Material UI */
import { Grid, Typography, Hidden, Snackbar, Alert } from '@mui/material';

/* React redux */
import { useDispatch, useSelector } from 'react-redux';

/* MomentJS */
import moment from 'moment';

/* React Big Calendar */
import { momentLocalizer } from 'react-big-calendar';

/* Project */
import CustomCalendar from 'content/features/calendar/CustomCalendar';
import useForm from 'hooks/useForm';
import { errorsValidation } from 'utils/functions';
import { getTechnicianPreselection, getUrlPreselection } from 'utils/urlPreselection';
import { setActiveStep } from 'content/app.actions';
import Loader from 'content/shared/Loader/Loader';
import SelectTechnician from './components/SelectTechnician';
import SelectTime from './components/SelectTime';
import StepsActionButtons from './components/StepsActionButtons';
import SchedulerStepsStyles from '../schedulerSteps/SchedulerSteps.styles';

import {
  controlsChanged,
  editDateError,
  closeEditDateError,
  emptyDateError,
  closeEmptyDateError,
  getTechniciansAndAvailabilities,
  getAvailabilities,
  setMonth,
} from './schedulerSteps.actions';
import LoaderDialog from '../loaders/LoaderDialog';
import ActionsButtonsContainer from './components/ActionsButtonsContainer';

function DateForm() {
  const dispatch = useDispatch();

  const controls = useSelector((state) => state.schedulerSteps.controls);

  const editError = useSelector((state) => state.schedulerSteps.editError);

  const dateError = useSelector((state) => state.schedulerSteps.dateError);

  const loading = useSelector((state) => state.schedulerSteps.loading);

  const calendarLoading = useSelector((state) => state.schedulerSteps.calendarLoading);

  const objects = useSelector((state) => state.app.objects);

  const holidays = useSelector((state) => state.app.objects.holidays);

  const selectedTechnician = useSelector(
    (state) => state.schedulerSteps.controls.selectedTechnician,
  );

  const reservations = useSelector((state) => state.schedulerSteps.controls.reservations);

  const technicianSchedule = useSelector(
    (state) => state.schedulerSteps.controls.technicianSchedule,
  );

  const welcomeScreenControls = useSelector((state) => state.welcomeScreen.controls);

  const {
    selectedDate,
    selectedHour,
    modelId,
    workshop,
    serviceType,
    month,
    selectedServiceCode,
    technicians,
  } = controls;

  const urlPreselection = useRef(getUrlPreselection());

  // The list left by a previous visit to this step is stale until the request below answers.
  const staleTechnicians = useRef(technicians);

  const [preselectedDate, setPreselectedDate] = useState('');

  const currentDate = moment().startOf('day').toDate();

  const { selectedService: welcomeScreensSelectedService } = welcomeScreenControls;

  const localizer = momentLocalizer(moment);

  const startDate =
    moment(currentDate).format('M') === moment(month).format('M')
      ? moment().startOf('day').utc().format()
      : moment(month).startOf('month').subtract(1, 'days').startOf('day').utc().format();
  const endDate = moment(month).endOf('month').endOf('day').utc().format();

  const selectedService =
    welcomeScreensSelectedService !== '' ? welcomeScreensSelectedService : selectedServiceCode;

  useEffect(() => {
    if (selectedTechnician === '') {
      dispatch(
        getTechniciansAndAvailabilities({
          loading: true,
          selectedService,
          workshopId: workshop,
          modelId,
          kmId: serviceType,
          diagnosisType: serviceType,
          startDate,
          endDate,
        }),
      );
    }
  }, [selectedService, workshop, modelId, serviceType, startDate, endDate, selectedTechnician]);

  const onChangeText = useCallback((id, value) => {
    dispatch(controlsChanged({ name: id, value }));
  }, []);

  const toggleEditError = () => {
    dispatch(editDateError());
  };

  const onCloseEditError = () => {
    dispatch(closeEditDateError());
  };

  const toggleEmptyError = () => {
    dispatch(emptyDateError());
  };

  const onCloseEmptyError = () => {
    dispatch(closeEmptyDateError());
  };

  const selectedDateValidation = (value) => {
    if (!value || value.toString().trim().length === 0) {
      return false;
    }

    const today = moment().startOf('day'); // Obtiene la fecha de hoy sin hora
    const selected = moment(value).startOf('day'); // Convierte la fecha seleccionada sin hora

    if (selected.isAfter(today)) {
      onCloseEmptyError();
      return true;
    } else {
      toggleEmptyError();
      return false;
    }
  };

  const { onChange, onSubmit, errors } = useForm(controls, null, {
    onChange: onChangeText,
    validations: {
      selectedDate: [
        {
          check: (value) => selectedDateValidation(value),
          message: 'Debe seleccionar una fecha posterior al dia de hoy',
        },
      ],
      selectedHour: [
        {
          check: (value) => value.toString().trim().length > 0,
          message: 'Debe seleccionar una hora',
        },
      ],
      selectedTechnician: [
        {
          check: (value) => value.toString().trim().length > 0,
          message: 'Debe seleccionar un técnico',
        },
      ],
    },
  });

  const onChangeDate = useCallback(
    (value) => {
      const event = { type: 'click', target: { name: 'selectedDate', value } };
      onChange(event);
    },
    [onChange],
  );

  const onChangeHour = useCallback(
    (value) => {
      const event = { type: 'click', target: { name: 'selectedHour', value } };
      onChange(event);
    },
    [onChange],
  );

  const callAvailabilities = (businessPartnerId, availabilitiesMonth = month) => {
    if (businessPartnerId !== '') {
      const startDate = moment(availabilitiesMonth).startOf('month').startOf('day').utc().format();
      const endDate = moment(availabilitiesMonth).endOf('month').endOf('day').utc().format();

      dispatch(
        getAvailabilities({
          calendarLoading: true,
          selectedService,
          startDate,
          endDate,
          technicianBusinessPartnerId: businessPartnerId,
        }),
      );
    }
  };

  const onChangeTechnician = useCallback(
    ({ target }) => {
      // A technician picked by hand drops the date of the link.
      setPreselectedDate('');
      onChangeHour('');
      dispatch(controlsChanged({ name: 'selectedDate', value: '' }));
      dispatch(controlsChanged({ name: 'selectedStartDate', value: '' }));
      dispatch(controlsChanged({ name: 'selectedEndDate', value: '' }));
      const selectedTechnicianData = {
        type: 'click',
        target: { name: 'selectedTechnician', value: target.value },
      };

      onChange(selectedTechnicianData);
      callAvailabilities(target.value.businessPartnerId);
    },
    [onChange],
  );

  // The link's preselection is checked once, against the first list answered after entering the
  // step. Its date is handed to the calendar, which selects it once the availability is loaded.
  useEffect(() => {
    if (!technicians.length || technicians === staleTechnicians.current) return;

    const { technician, date } = getTechnicianPreselection(technicians, urlPreselection.current);
    urlPreselection.current = {};

    if (!technician) return;

    onChange({ type: 'click', target: { name: 'selectedTechnician', value: technician } });
    if (date) {
      dispatch(setMonth({ month: moment(date) }));
      setPreselectedDate(date);
    }
    callAvailabilities(technician.businessPartnerId, date || month);
  }, [technicians]);

  const handleNext = (event) => {
    event.preventDefault();
    onSubmit(() => dispatch(setActiveStep()));
  };

  const technicianField = (
    <SelectTechnician
      controls={controls}
      onChange={onChangeTechnician}
      errors={errors}
      objects={objects}
      calendarLoading={calendarLoading}
    />
  );

  const formValid = errorsValidation(errors);

  return (
    <Loader loading={loading} height={300}>
      <form style={{ width: '100%' }} onSubmit={handleNext}>
        <Hidden>
          <Typography color="primary" variant="h6" gutterBottom sx={{ marginBottom: '20px' }}>
            1. Selecciona técnico
          </Typography>
        </Hidden>
        <Hidden mdDown>{technicianField}</Hidden>
        <Hidden mdUp>{technicianField}</Hidden>

        {/* <Hidden mdUp>
          <Typography color="primary" variant="h6" gutterBottom sx={{ marginBottom: '20px' }}>
            SELECCIONAR HORARIO
          </Typography>
        </Hidden> */}
        <Grid container direction="row" justifyContent="flex-start">
          <Grid item xs={12} sm={12} md={6} boxSizing="border-box" pr={2}>
            <Typography color="primary" variant="h6" gutterBottom sx={{ marginBottom: '20px' }}>
              2. Selecciona fecha
            </Typography>
            <CustomCalendar
              date={selectedDate}
              setDate={onChangeDate}
              currentDate={currentDate}
              controls={controls}
              setSelectHour={onChangeHour}
              month={month}
              calendarLoading={calendarLoading}
              holidays={holidays}
              reservations={reservations}
              technicianSchedule={technicianSchedule}
              preselectedDate={preselectedDate}
              onPreselectedDate={() => setPreselectedDate('')}
            />
          </Grid>
          <Grid item xs={12} sm={12} md={6} pl={2} boxSizing="border-box">
            <Grid>
              <Hidden>
                <Typography color="primary" variant="h6" gutterBottom sx={{ marginBottom: '20px' }}>
                  3. Horarios disponibles
                </Typography>
              </Hidden>
              <SelectTime
                date={selectedDate}
                selectHour={selectedHour}
                setSelectHour={onChangeHour}
                controls={controls}
                errors={errors}
                calendarLoading={calendarLoading}
                reservations={reservations}
                technicianSchedule={technicianSchedule}
              />
              <Grid sx={{ marginTop: '20px' }}>
                <Grid sx={SchedulerStepsStyles.infoContainer}>
                  <Grid sx={SchedulerStepsStyles.availableBox} />
                  <Typography color="primary" variant="body2" sx={SchedulerStepsStyles.infoText}>
                    Disponible
                  </Typography>
                </Grid>
                <Grid sx={SchedulerStepsStyles.infoContainer}>
                  <Grid sx={SchedulerStepsStyles.selectedBox} />
                  <Typography color="primary" variant="body2" sx={SchedulerStepsStyles.infoText}>
                    Seleccionado
                  </Typography>
                </Grid>
                <Grid sx={SchedulerStepsStyles.infoContainer}>
                  <Grid sx={SchedulerStepsStyles.unavailableBox} />
                  <Typography color="primary" variant="body2" sx={SchedulerStepsStyles.infoText}>
                    No disponible
                  </Typography>
                </Grid>
              </Grid>
            </Grid>
          </Grid>

          <Snackbar
            open={dateError}
            onClose={onCloseEmptyError}
            autoHideDuration={2800}
            anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
            <Alert variant="filled" severity="error" sx={{ width: '100%' }}>
              {errors.selectedDate[0]}
            </Alert>
          </Snackbar>
          <Snackbar
            open={editError}
            onClose={onCloseEditError}
            autoHideDuration={2800}
            anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
            <Alert variant="filled" severity="error" sx={{ width: '100%' }}>
              Para cambiar la fecha presioné el botón editar en la fecha seleccionada.
            </Alert>
          </Snackbar>
        </Grid>
        <ActionsButtonsContainer>
          <StepsActionButtons handleNext={handleNext} disabled={formValid} clearDateOnBack />
        </ActionsButtonsContainer>
      </form>
      <LoaderDialog open={calendarLoading} title="Buscando reservas técnico" />
    </Loader>
  );
}

export default DateForm;
